import { isNodeV2, type NodeV2 } from '@vedalib/editor'
import { useMemo } from 'react'
import React from 'react'

import { useProjectContext } from 'services/Store/Project/hooks'
import {
  getDragFull,
  getHighlighted,
  getNodeStyle,
  mergeNodeStyle,
} from 'services/Store/Project/selectors'
import type { Block, IBlockMode } from 'services/Store/Project/types'

import { safeParseCoords } from '../../formControls/Coords/utils'
import { safeParsePxSizes } from '../../formControls/Sizes/utils'
import SvgLine from './SvgLine'
import SvgPosition from './SvgPosition'
import SvgText from './SvgText'

interface ISvgLayoutAbsoluteProps {
  mode: IBlockMode
  block: Block
  node: NodeV2
  selected: NodeV2[]
  size: { width: number; height: number }
}

const SvgLayoutAbsolute: React.FC<ISvgLayoutAbsoluteProps> = ({
  mode,
  block,
  node,
  selected,
  size,
}) => {
  const dnd = useProjectContext(getDragFull)
  const highlighted = useProjectContext(getHighlighted)
  const style = useProjectContext(getNodeStyle, block.uuid, node.id)
  const offset = useMemo(() => {
    const [top, right, bottom, left] = safeParsePxSizes(style?.margin)
    return [top || 0, right || 0, bottom || 0, left || 0] satisfies [number, number, number, number]
  }, [style?.margin])
  const { top, left, right, bottom } = useMemo(() => {
    const coords = Object.values(dnd?.coords || {})
    const top = Math.min(...coords.map((i) => i.top ?? NaN))
    const left = Math.min(...coords.map((i) => i.left ?? NaN))
    const right = Math.min(...coords.map((i) => i.right ?? NaN))
    const bottom = Math.min(...coords.map((i) => i.bottom ?? NaN))

    return {
      top: isNaN(top) ? undefined : top,
      left: isNaN(left) ? undefined : left,
      right: isNaN(right) ? undefined : right,
      bottom: isNaN(bottom) ? undefined : bottom,
    }
  }, [dnd?.coords])

  const coords = useMemo(
    () =>
      node.children
        .map((childId) => block.schema.nodes[childId])
        .filter(isNodeV2)
        .filter(
          (child) =>
            !dnd?.targetIds.includes(child.id) &&
            (highlighted === child.id || selected.some((sel) => sel.id === child.id)),
        )
        .map((child) => {
          const style = mergeNodeStyle(child.style, mode.deviceMode)
          const coords = safeParseCoords(style?.coords)
          return { id: child.id, ...coords }
        }),
    [node.children, block.schema.nodes, dnd?.targetIds, highlighted, selected, mode.deviceMode],
  )

  return (
    <>
      {dnd && dnd.type === 'DRAG' && (
        <SvgPosition
          bottom={bottom}
          left={left}
          offset={offset}
          right={right}
          size={size}
          top={top}
        />
      )}

      {dnd?.guides?.map(({ id, color, x1, x2, y1, y2, helper }) => (
        <React.Fragment key={id}>
          <SvgLine color={color} x1={x1} x2={x2} y1={y1} y2={y2} />
          {helper && (
            <>
              <SvgLine color={color} x1={helper.x1} x2={helper.x2} y1={helper.y1} y2={helper.y2} />
              <SvgText
                border={color}
                color={color}
                text={helper.text}
                x={(helper.x1 + helper.x2) / 2}
                y={(helper.y1 + helper.y2) / 2}
              />
            </>
          )}
        </React.Fragment>
      ))}

      {coords.map(({ id, top, left, right, bottom }) => (
        <React.Fragment key={id}>
          <SvgPosition
            bottom={bottom}
            key={id}
            left={left}
            offset={offset}
            right={right}
            size={size}
            top={top}
          />
        </React.Fragment>
      ))}
    </>
  )
}

export default SvgLayoutAbsolute
